import * as Yup from "yup";
import { SwiftSavingsDetails } from "../types/swiftSavings";
import {
  CurrentDetailsPayload,
  GuardianDetailsPayload,
  MinorDetailsPayload,
  MinorDocsPayload,
  RegularSavingsDetailsPayload,
  RegularSavingsDocsPayload,
  SubmitSwiftDocsPayload,
} from "../models/IEnums.model";
import {
  idCardErrorMsg,
  idCardValidationRegex,
  idValidation,
} from "./constants";
import {
  AcctNoNin,
  CustomerDocData,
  EmploymentDetailsData,
  ProofOfAddressData,
  TierType,
} from "../types/upgradeAccount";

export const swiftDetailsValidationSchema: Yup.SchemaOf<SwiftSavingsDetails> =
  Yup.object({
    firstName: Yup.string(),
    middleName: Yup.string(),
    lastName: Yup.string(),
    phoneNumber: Yup.string(),
    emailAddress: Yup.string().email("Invalid email"),
    dob: Yup.string(),
    gender: Yup.string().required("Gender is required"),
    houseNumber: Yup.string().required("House number is required"),
    streetName: Yup.string().required("Street name is required"),
    areaState: Yup.string().required("Area/State is required"),
    nearestBustopLandmark: Yup.string().required(
      "Nearest bus stop/landmark is required",
    ),
    address: Yup.string()
      .max(50, "Address should not be more than 50 characters")
      .required("Address is required"),
    religion: Yup.number().required("Religion is required"),
    occupation: Yup.number().required("Occupation is required"),
    referralCode: Yup.string(),
    bvn: Yup.string(),
    nin: Yup.string(),
    socialMediaId: Yup.number(),
    socialMediaHandle: Yup.string(),
    town: Yup.string().required("Town is required"),
    localGovernment: Yup.string().required("Local Government Area is required"),
    nextOfKin: Yup.string().required("Next of kin is required"),
    relationshipOfNextOfKin: Yup.string().required(
      "Relationship to next of kin is required",
    ),
    employmentInformation: Yup.string().required(
      "Employment status is required",
    ),
    branchOfDomiciliation: Yup.number()
      .min(1, "Branch of domiciliation is required")
      .required("Branch of domiciliation is required"),
    termsCondition: Yup.boolean().oneOf(
      [true],
      "You must accept the terms and conditions",
    ),
  }).test(
    "bvn-or-nin",
    "Either BVN or NIN is required (but not both)",
    (values) => {
      const hasBVN = !!values.bvn;
      const hasNIN = !!values.nin;
      return hasBVN !== hasNIN;
    },
  );

export const submitSwiftDocsValidationSchema: Yup.SchemaOf<SubmitSwiftDocsPayload> =
  Yup.object({
    id: Yup.number(),
    imageUrl: Yup.string(),
    signatureUrl: Yup.string().required("Signature is required"),
    debitCardValueAddedService: Yup.boolean().oneOf([true, false]),
    accountTypecode: Yup.string(),
    accountTypeName: Yup.string(),
  });

export const regularDocsValidationSchema: Yup.SchemaOf<RegularSavingsDocsPayload> =
  Yup.object({
    id: Yup.number().required(),
    signatureUrl: Yup.string().required("Signature is required"),
    imageUrl: Yup.string(),
    frontPageIdUrl: Yup.string().required("Front page ID is required"),
    debitCardValueAddedService: Yup.boolean(),
    accountTypecode: Yup.string().required("Account type code is required"),
    accountTypeName: Yup.string().required("Account type name is required"),
    utilityBill: Yup.string().required("Utility bill is required"),
    identityCardType: Yup.number()
      .oneOf([2, 3, 5, 8], "Invalid identity card type")
      .required("Identity card type is required"),
    idCardNumber: Yup.string()
      .required("ID card number is required")
      .when(
        "identityCardType",
        (identityCardType: number, schema: Yup.StringSchema) => {
          return (
            schema.matches(
              idCardValidationRegex[identityCardType],
              idCardErrorMsg[identityCardType],
            ) || schema
          );
        },
      ),
    issueDate: Yup.string(),
    expiryDate: Yup.string(),
    // .nullable()
    // .when("identityCardType", {
    //   is: (value: number) => value === 2 || value === 3, // Passport & NIN require expiry date
    //   then: (schema) => schema.required("Expiry date is required"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
  });

export const domDocsValidationSchema = regularDocsValidationSchema
  .shape({
    currencyOfAccountEURO: Yup.string(),
    currencyOfAccountGBP: Yup.string(),
    currencyOfAccountUSD: Yup.string(),
  })
  .test("only-one-currency", "Currency is required", (values) => {
    const {
      currencyOfAccountEURO,
      currencyOfAccountGBP,
      currencyOfAccountUSD,
    } = values;
    const filledFields = [
      currencyOfAccountEURO,
      currencyOfAccountGBP,
      currencyOfAccountUSD,
    ].filter(Boolean);
    return filledFields.length === 1; // Ensures exactly one field is filled
  });

export const guardianDetailsValidationSchema: Yup.SchemaOf<GuardianDetailsPayload> =
  Yup.object({
    emailAddress: Yup.string().email("Invalid email"),
    country: Yup.number()
      .min(1, "Country is required")
      .required("Country is required"),
    dob: Yup.string(),
    firstName: Yup.string(),
    lastName: Yup.string(),
    maidenName: Yup.string(),
    middleName: Yup.string(),
    socialMediaHandle: Yup.string(),
    socialMediaId: Yup.number(),
    phoneNumber: Yup.string(),
    relationship: Yup.string(),
    title: Yup.string().required("Title is required"),
    address: Yup.string()
      .max(50, "Address should not be more than 50 characters")
      .required("Address is required"),
    religion: Yup.number()
      .min(1, "Religion is required")
      .required("Religion is required"),
    referralCode: Yup.string(),
    bvn: Yup.string(),
    town: Yup.string().required("Town is required"),
    localGovernment: Yup.string().required("Local Government Area is required"),
    branchOfDomiciliation: Yup.number()
      .min(1, "Branch of domiciliation is required")
      .required("Branch of domiciliation is required"),
    termsCondition: Yup.boolean().oneOf(
      [true],
      "You must accept the terms and conditions",
    ),
    gender: Yup.string().required("Gender is required"),
    houseNumber: Yup.string().required("House number is required"),
    streetName: Yup.string().required("Street name is required"),
    areaState: Yup.string().required("Area/State is required"),
    nearestBustopLandmark: Yup.string().required(
      "Nearest bus stop/landmark is required",
    ),
  });

export const minorDetailsValidationSchema: Yup.SchemaOf<MinorDetailsPayload> =
  Yup.object({
    minorAddress: Yup.string()
      .max(50, "Address should not be more than 50 characters")
      .required("Minor's address is required"),
    minorBirthCertificate: Yup.string().required(
      "Minor's birth certificate is required",
    ),
    minorNearestBusStop: Yup.string().required(
      "Minor's nearest busstop is required",
    ),
    minorDob: Yup.string().required("Minor's date of birth is required"),
    minorEmail: Yup.string(),
    minorFirstName: Yup.string(),
    minorFullName: Yup.string().required("Minor's full name is required"),
    minorHouseNumber: Yup.string().required("Minor's house number is required"),
    minorLastName: Yup.string(),
    minorLGA: Yup.string().required("Minor's LGA is required"),
    minorMiddleName: Yup.string(),
    minorNIN: Yup.string()
      .matches(/^$|^\d{11}$/, "NIN must be 11 digits")
      .notRequired(),

    minorPassport: Yup.string().required(
      "Minor's passport photograph is required",
    ),

    minorPhoneNumber: Yup.string()
      .matches(/^$|^\d{11}$/, "Phone number must be 11 digits")
      .notRequired(),

    minorReligion: Yup.number()
      .min(1, "Minor's religion is required")
      .required("Minor's religion is required"),

    minorState: Yup.string().required("Minor's state is required"),
    minorStreetName: Yup.string().required("Minor's street name is required"),
  });

export const minorDocsValidationSchema: Yup.SchemaOf<MinorDocsPayload> =
  Yup.object({
    id: Yup.number(),
    signatureUrl: Yup.string().required("Signature is required"),
    imageUrl: Yup.string(),
    minorIdentityUrl: Yup.string(),
    accountTypecode: Yup.string(),
    accountTypeName: Yup.string(),
    utilityBill: Yup.string().required("Utility bill is required"),
    handoverAccount: Yup.boolean(),
  });

export const regularDetailsValidationSchema: Yup.SchemaOf<RegularSavingsDetailsPayload> =
  Yup.object({
    firstName: Yup.string(),
    middleName: Yup.string(),
    lastName: Yup.string(),
    phoneNumber: Yup.string(),
    emailAddress: Yup.string().email("Invalid email"),
    dob: Yup.string(),
    gender: Yup.string().required("Gender is required"),
    houseNumber: Yup.string().required("House number is required"),
    streetName: Yup.string().required("Street name is required"),
    areaState: Yup.string().required("Area/State is required"),
    nearestBustopLandmark: Yup.string().required(
      "Nearest bus stop/landmark is required",
    ),
    address: Yup.string()
      .max(50, "Address should not be more than 50 characters")
      .required("Address is required"),
    religion: Yup.number().required("Religion is required"),
    occupation: Yup.number()
      .min(1, "Occupation is required")
      .required("Occupation is required"),
    referralCode: Yup.string(),
    bvn: Yup.string(),
    nin: Yup.string(),
    socialMediaId: Yup.number(),
    socialMediaHandle: Yup.string(),
    town: Yup.string().required("Town is required"),
    localGovernment: Yup.string().required("Local Government Area is required"),
    nameOfNextOfKin: Yup.string().required("Next of kin is required"),
    relationshipOfNextOfKin: Yup.string().required(
      "Relationship to next of kin is required",
    ),
    selfEmployementInformation: Yup.number()
      .min(1, "Employment status is required")
      .required("Employment status is required"),
    branchOfDomiciliation: Yup.number()
      .min(1, "Branch of domiciliation is required")
      .required("Branch of domiciliation is required"),
    termsCondition: Yup.boolean().oneOf(
      [true],
      "You must accept the terms and conditions",
    ),
  });

export const currentDetailsValidationSchema: Yup.SchemaOf<CurrentDetailsPayload> =
  Yup.object({
    emailAddress: Yup.string().email("Invalid email"),
    address: Yup.string()
      .max(50, "Address should not be more than 50 characters")
      .required("Address is required"),
    occupation: Yup.number()
      .min(1, "Occupation is required")
      .required("Occupation is required"),
    idCardNumber: Yup.string()
      // .required("Id card number is required")
      .length(
        idValidation[2].length,
        `Id card number must be ${idValidation[2].length} characters`,
      ),
    relationshipOfNextOfKin: Yup.string().required(
      "Relationship to Next Of Kin is required",
    ),
    selfEmployementInformation: Yup.number()
      .min(1, "Employment information is required")
      .required("Employment information is required"),
    religion: Yup.string().required("Religion is required"),
    areaState: Yup.string().required("Area is required"),
    nextOfKin: Yup.string().required("Next of kin is required"),
    streetName: Yup.string().required("Street name is required"),
    localGovernment: Yup.string().required("Local Government Area is required"),
    town: Yup.string().required("Town is required"),
    houseNumber: Yup.string().required("House number is required"),
    nearestBustopLandmark: Yup.string().required(
      "Nearest bus stop is required",
    ),
    gender: Yup.string().required("Please Select a gender"),
    branchOfDomiciliation: Yup.number()
      .min(1, "Branch Of Domiciliation is required")
      .required("Branch Of Domiciliation is required"),
    socialMediaId: Yup.number(),
    socialMediaHandle: Yup.string(),
    bvn: Yup.string(),
    dateOfBirth: Yup.string(),
    firstName: Yup.string(),
    lastName: Yup.string(),
    phoneNumber: Yup.string(),
    referralCode: Yup.string(),
    secondName: Yup.string(),
    termsCondition: Yup.boolean(),
  });

export const acctNoNinSchema: Yup.SchemaOf<AcctNoNin> = Yup.object({
  accountNumber: Yup.string()
    .required("Account number is required")
    .matches(/^\d{10}$/, "Account number must be exactly 10 digits"),

  nin: Yup.string()
    .required("NIN is required")
    .matches(/^\d{11}$/, "NIN must be exactly 11 digits"),
  tierType: Yup.mixed<TierType>().oneOf([2, 3]).required(),
});

export const getCustomerDetailsValidationSchema = (
  tierType: TierType,
): Yup.SchemaOf<EmploymentDetailsData & ProofOfAddressData> => {
  const addressField = (fieldName: string) =>
    tierType === 3
      ? Yup.string().required(`${fieldName} is required`)
      : Yup.string().notRequired();

  return Yup.object({
    // Employment
    selectEmploymentStatus: Yup.string().required(
      "Employment status is required",
    ),

    positionHeld: Yup.string().when("selectEmploymentStatus", {
      is: "Employed",
      then: (schema) => schema.required("Position held is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    employerName: Yup.string().when("selectEmploymentStatus", {
      is: "Employed",
      then: (schema) => schema.required("Employer name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    salaryInfo: Yup.string().when("selectEmploymentStatus", {
      is: "Employed",
      then: (schema) => schema.required("Salary information is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    employerAddress: Yup.string().when("selectEmploymentStatus", {
      is: "Employed",
      then: (schema) => schema.required("Employer address is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    natureofBusiness: Yup.string().when("selectEmploymentStatus", {
      is: "Self employed",
      then: (schema) => schema.required("Nature of business is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    businessAddress: Yup.string().when("selectEmploymentStatus", {
      is: "Self employed",
      then: (schema) => schema.required("Business address is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Proof of Address
    houseNumber: addressField("House number"),
    streetName: addressField("Street name"),
    nearestLandMark: addressField("Nearest landmark"),
    lga: addressField("LGA"),
    state: addressField("State"),
    town: addressField("Town"),
  });
};

export const getMeansOfIdSchema = (
  tierType: TierType,
): Yup.SchemaOf<CustomerDocData> => {
  const addressField = (fieldName: string) =>
    tierType === 3
      ? Yup.string().required(`${fieldName} is required`)
      : Yup.string().notRequired();

  return Yup.object({
    ninUrl: Yup.string().required("Front page of NIN card is required"),
    utilityBill: addressField("Utility bill"),
  });
};
